/**
 * /app/compras/lotes — lotes y vencimientos (FEFO interno).
 * Vista de batches activos, próximos a vencer y consumidos.
 * Gateado por supply_chain.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { FeatureLockedState } from "@/components/workspace/FeatureLockedState";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { DataTable, CrudEmptyState, Loading, QuickActions } from "@/components/ds";
import type { DataTableColumn, QuickAction } from "@/components/ds";
import { apiGetJson } from "@/lib/sm-api";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import { useTenantContext } from "@/lib/tenant-context";
import { insumosCopy, loteStatusLabel } from "@/lib/merchant-feature-copy";
import type { PlanApiErrorPayload } from "@structura/core/plan-api-errors";
import { toast } from "sonner";

export const Route = createFileRoute("/app/compras/lotes")({
  component: LotesPage,
  head: () => ({ meta: [{ title: "Lotes y vencimientos" }] }),
});

interface StockBatch {
  id: string;
  ingredientId: string;
  ingredientNombre?: string;
  cantidad: number;
  cantidadRestante: number;
  costoUnitario: number | null;
  venceAt: string | null;
  status: string;
  loteExterno: string | null;
  createdAt: string;
}

const DAYS_WARNING = 7;

function LotesPage() {
  const tid = useWorkspaceTenantId();
  const { businessProfile } = useTenantContext();
  const insumos = insumosCopy(businessProfile);
  const [batches, setBatches] = useState<StockBatch[]>([]);
  const [loading, setLoading] = useState(true);
  const [lock, setLock] = useState<PlanApiErrorPayload | null>(null);
  const [filter, setFilter] = useState<"all" | "expiry_soon" | "expired">("all");

  const load = async () => {
    if (!tid) return;
    setLoading(true);
    try {
      const r = await apiGetJson<{ batches?: StockBatch[]; _lock?: PlanApiErrorPayload }>(
        `/api/supply-chain?resource=batches`,
        tid,
      );
      if (r._lock) {
        setLock(r._lock);
        return;
      }
      setBatches(r.batches ?? []);
    } catch {
      toast.error("Error al cargar lotes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [tid]);

  const today = new Date();
  const warningDate = new Date(today.getTime() + DAYS_WARNING * 24 * 60 * 60 * 1000);

  const batchesFiltered = batches.filter((b) => {
    if (filter === "all") return b.status === "active";
    if (filter === "expiry_soon") {
      if (!b.venceAt) return false;
      const exp = new Date(b.venceAt);
      return b.status === "active" && exp <= warningDate && exp >= today;
    }
    if (filter === "expired") {
      if (!b.venceAt) return false;
      return new Date(b.venceAt) < today;
    }
    return true;
  });

  const expirySoonCount = batches.filter((b) => {
    if (!b.venceAt || b.status !== "active") return false;
    const exp = new Date(b.venceAt);
    return exp <= warningDate && exp >= today;
  }).length;

  const expiredCount = batches.filter(
    (b) => b.venceAt && new Date(b.venceAt) < today && b.status === "active",
  ).length;

  const columns: DataTableColumn<StockBatch>[] = [
    { key: "ingrediente", header: insumos.itemLabel, render: (b) => <span className="font-medium">{b.ingredientNombre ?? b.ingredientId}</span> },
    { key: "lote", header: "Lote externo", render: (b) => <span className="text-muted-foreground text-xs font-mono">{b.loteExterno ?? "—"}</span> },
    {
      key: "cantidad",
      header: "Cantidad inicial",
      className: "text-right",
      render: (b) => <span className="tabular-nums">{b.cantidad.toLocaleString("es-AR")}</span>,
    },
    {
      key: "restante",
      header: "Restante",
      className: "text-right",
      render: (b) => {
        const pct = b.cantidad > 0 ? Math.round((b.cantidadRestante / b.cantidad) * 100) : 0;
        return (
          <div className="flex flex-col items-end gap-0.5">
            <span className="tabular-nums font-medium">{b.cantidadRestante.toLocaleString("es-AR")}</span>
            <div className="h-1 w-16 rounded-full bg-muted/40">
              <div
                className={`h-1 rounded-full ${pct > 50 ? "bg-green-500" : pct > 20 ? "bg-amber-500" : "bg-red-500"}`}
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        );
      },
    },
    {
      key: "vencimiento",
      header: "Vencimiento",
      render: (b) => {
        const vence = b.venceAt ? new Date(b.venceAt) : null;
        const isExpired = vence && vence < today;
        const isSoon = vence && !isExpired && vence <= warningDate;
        return vence ? (
          <span className={`text-xs ${isExpired ? "text-red-400" : isSoon ? "text-amber-400" : "text-muted-foreground"}`}>
            {vence.toLocaleDateString("es-AR")}
            {isSoon && <span className="ml-1">⚠️</span>}
            {isExpired && <span className="ml-1">🔴</span>}
          </span>
        ) : (
          <span className="text-xs text-muted-foreground">Sin vto.</span>
        );
      },
    },
    {
      key: "estado",
      header: "Estado",
      render: (b) => (
        <Badge
          variant="outline"
          className={`text-[10px] ${b.status === "active" ? "text-green-400 border-green-500/30" : "text-muted-foreground"}`}
        >
          {loteStatusLabel(b.status)}
        </Badge>
      ),
    },
  ];

  const headerActions: QuickAction[] = [
    { key: "refresh", label: "Actualizar", icon: RefreshCw, variant: "ghost", onClick: load },
  ];

  if (loading) return <Loading variant="list" rows={3} />;
  if (lock) return <FeatureLockedState payload={lock} />;

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <QuickActions actions={headerActions} />
      </div>

      {/* Alertas de vencimiento */}
      {(expirySoonCount > 0 || expiredCount > 0) && (
        <div className="space-y-2">
          {expiredCount > 0 && (
            <div className="flex items-center gap-2 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-400">
              <AlertTriangle className="h-4 w-4 flex-shrink-0" />
              <span>
                {expiredCount} lote{expiredCount > 1 ? "s" : ""} vencido{expiredCount > 1 ? "s" : ""}
              </span>
              <button onClick={() => setFilter("expired")} className="ml-auto underline text-xs">
                Ver
              </button>
            </div>
          )}
          {expirySoonCount > 0 && (
            <div className="flex items-center gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-400">
              <AlertTriangle className="h-4 w-4 flex-shrink-0" />
              <span>
                {expirySoonCount} lote{expirySoonCount > 1 ? "s" : ""} próximo{expirySoonCount > 1 ? "s" : ""}{" "}
                a vencer (≤{DAYS_WARNING}d)
              </span>
              <button onClick={() => setFilter("expiry_soon")} className="ml-auto underline text-xs">
                Ver
              </button>
            </div>
          )}
        </div>
      )}

      {/* Filtros de vencimiento (sub-filtro de Lotes, no tabs de sección) */}
      <div className="flex gap-2 overflow-x-auto rounded-lg border border-border/40 bg-card/30 px-1">
        {(
          [
            { key: "all", label: "Activos" },
            { key: "expiry_soon", label: `Por vencer (${expirySoonCount})` },
            { key: "expired", label: `Vencidos (${expiredCount})` },
          ] as const
        ).map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`flex-shrink-0 rounded-md px-3 py-1.5 text-sm transition-colors ${filter === f.key ? "bg-primary/15 text-foreground" : "text-muted-foreground hover:text-foreground"}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <DataTable
        columns={columns}
        rows={batchesFiltered}
        getRowKey={(b) => b.id}
        emptyState={<CrudEmptyState title="Sin lotes en esta categoría" />}
      />
    </div>
  );
}
